import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, MessageCircle, Trash2, LogOut, RefreshCw,
  MailOpen, Mail as MailIcon, Inbox, Loader2, Radar,
  UserPlus, ChevronDown, Save, Plus, X, Calendar, CalendarX, Link2, Check, Copy,
  BarChart3, Trophy, Medal, FileDown,
  Pencil, ArrowUp, ArrowDown, Users, Eye,
} from "lucide-react";
import LoginForm from "@/components/LoginForm";
import { SectionLabel } from "@/components/Reveal";
import { CATEGORIES as BLOG_CATEGORIES } from "@/articlesData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const levelColor = (v) =>
  v < 40 ? "text-brand-red" : v < 60 ? "text-brand-orange" : v < 80 ? "text-slate-300" : "text-emerald-400";

const wppLink = (raw, name) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return null;
  const full = digits.startsWith("55") ? digits : `55${digits}`;
  return `https://wa.me/${full}?text=${encodeURIComponent(`Olá ${name}, aqui é a Danielle, da FUCHS. Recebi sua mensagem pelo site.`)}`;
};

const mailLink = (m) =>
  `mailto:${m.email}?subject=${encodeURIComponent(`Re: Sua mensagem para a FUCHS`)}&body=${encodeURIComponent(
    `Olá ${m.name},\n\nObrigada pelo contato. \n\nAbraço,\nDanielle Fuchs\n\n—\nMensagem original (${m.company || "sem empresa"}):\n${m.challenge}`
  )}`;

const diagFirstName = (d) => (d.name ? d.name.trim().split(" ")[0] : "");

const diagScoresText = (d) =>
  Object.entries(d.scores || {})
    .map(([k, v]) => `${k} ${v}`)
    .join(" · ");

const diagMailLink = (d) =>
  `mailto:${d.email}?subject=${encodeURIComponent("Seu Diagnóstico 4D — FUCHS")}&body=${encodeURIComponent(
    `Olá ${diagFirstName(d)},\n\nRecebi o seu Diagnóstico de Maturidade 4D pelo site.\nÍndice geral: ${d.overall}/100\n${diagScoresText(d)}\n\nVamos conversar sobre a leitura do seu radar?\n\nAbraço,\nDanielle Fuchs`
  )}`;

const diagWppLink = (d) => {
  const digits = (d.whatsapp || "").replace(/\D/g, "");
  if (!digits) return null;
  const full = digits.startsWith("55") ? digits : `55${digits}`;
  return `https://wa.me/${full}?text=${encodeURIComponent(
    `Olá ${diagFirstName(d)}, aqui é a Danielle, da FUCHS. Vi seu Diagnóstico 4D: índice geral ${d.overall}/100 (${diagScoresText(d)}). Vamos conversar sobre a leitura do seu radar?`
  )}`;
};

const MessageCard = ({ m, onToggleRead, onDelete }) => {
  const wpp = wppLink(m.whatsapp, m.name);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -24 }}
      data-testid={`message-card-${m.id}`}
      className={`border p-6 transition-colors duration-300 lg:p-8 ${
        m.read ? "border-white/10 bg-navy/20" : "border-brand-orange/40 bg-navy/50"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <span
            className={`mt-1 flex h-10 w-10 shrink-0 items-center justify-center border ${
              m.read ? "border-white/15 text-slate-500" : "border-brand-orange/60 text-brand-orange"
            }`}
          >
            {m.read ? <MailOpen size={16} /> : <MailIcon size={16} />}
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="font-display text-lg font-bold text-white">{m.name}</h3>
              {!m.read && (
                <span className="bg-brand-orange px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-white">
                  Nova
                </span>
              )}
            </div>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
              {m.company || "Sem empresa"} · {fmtDate(m.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={mailLink(m)}
            data-testid={`message-reply-email-${m.id}`}
            title="Responder por e-mail"
            className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-brand-orange hover:text-brand-orange"
          >
            <Mail size={15} />
          </a>
          {wpp && (
            <a
              href={wpp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`message-reply-whatsapp-${m.id}`}
              title="Responder pelo WhatsApp"
              className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-brand-orange hover:text-brand-orange"
            >
              <MessageCircle size={15} />
            </a>
          )}
          <button
            onClick={() => onToggleRead(m)}
            data-testid={`message-toggle-read-${m.id}`}
            title={m.read ? "Marcar como não lida" : "Marcar como lida"}
            className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-brand-orange hover:text-brand-orange"
          >
            {m.read ? <MailIcon size={15} /> : <MailOpen size={15} />}
          </button>
          <button
            onClick={() => onDelete(m)}
            data-testid={`message-delete-${m.id}`}
            title="Excluir mensagem"
            className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-brand-red hover:text-brand-red"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      <div className="mt-5 border-l-2 border-white/15 pl-5">
        <p className="whitespace-pre-line text-sm leading-relaxed text-slate-300">{m.challenge}</p>
        <p className="mt-3 text-sm text-slate-500">
          <span className="text-slate-400">{m.email}</span>
          {m.whatsapp && <span> · {m.whatsapp}</span>}
        </p>
      </div>
    </motion.div>
  );
};

const DiagnosticCard = ({ d }) => {
  const wpp = diagWppLink(d);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`diagnostic-card-${d.id}`}
      className="border border-white/10 bg-navy/30 p-6 lg:p-8"
    >
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          <span className="flex h-12 w-12 items-center justify-center border border-brand-orange/50 text-brand-orange">
            <Radar size={20} />
          </span>
          <div>
            <p className="font-display text-3xl font-extrabold text-white">
              {d.overall}
              <span className="font-mono text-xs font-normal text-slate-500"> /100</span>
            </p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
              {fmtDate(d.created_at)}
            </p>
            {d.name && (
              <p className="mt-2 text-sm text-slate-300" data-testid={`diagnostic-lead-${d.id}`}>
                <span className="font-semibold text-white">{d.name}</span>
                {d.email && <span className="text-slate-400"> · {d.email}</span>}
                {d.whatsapp && <span className="text-slate-400"> · {d.whatsapp}</span>}
              </p>
            )}
            {d.source && (
              <p className="mt-2" data-testid={`diagnostic-source-${d.id}`}>
                <span className="border border-brand-magenta/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-brand-magenta">
                  via {d.source}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          {(d.email || wpp) && (
            <div className="flex items-center gap-2">
              {d.email && (
                <a
                  href={diagMailLink(d)}
                  data-testid={`diagnostic-reply-email-${d.id}`}
                  title="Responder por e-mail"
                  className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-brand-orange hover:text-brand-orange"
                >
                  <Mail size={15} />
                </a>
              )}
              {wpp && (
                <a
                  href={wpp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`diagnostic-reply-whatsapp-${d.id}`}
                  title="Responder pelo WhatsApp"
                  className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-brand-orange hover:text-brand-orange"
                >
                  <MessageCircle size={15} />
                </a>
              )}
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            {Object.entries(d.scores || {}).map(([k, v]) => (
              <span
                key={k}
                className="border border-white/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400"
              >
                {k} <span className={`font-semibold ${levelColor(v)}`}>{v}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ---------- Clients tab ----------

const PLANS = ["Plataforma Completa", "Etapas únicas", "Conselho Estratégico de Marca"];
const STAGE_KEYS = ["D1", "D2", "D3", "D4"];

const emptyStages = () =>
  Object.fromEntries(STAGE_KEYS.map((k) => [k, { status: "pendente", start: "", end: "", deliverables: [] }]));

const emptyProject = (clientId) => ({
  client_id: clientId,
  company: "",
  plan: PLANS[0],
  value: "",
  start_date: "",
  status: "ativo",
  phone: "",
  cnpj: "",
  financial_contact: "",
  logo: "",
  stages: emptyStages(),
});

const inputCls =
  "w-full border border-white/15 bg-ink/60 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-brand-orange";
const selectCls = inputCls + " appearance-none";

const ClientEditor = ({ client, draft, onChange, onSave, saving }) => {
  const setField = (f, v) => onChange({ ...draft, [f]: v });
  const setStage = (k, f, v) =>
    onChange({ ...draft, stages: { ...draft.stages, [k]: { ...draft.stages[k], [f]: v } } });
  const setDeliv = (k, idx, f, v) => {
    const list = [...(draft.stages[k].deliverables || [])];
    list[idx] = { ...list[idx], [f]: v };
    setStage(k, "deliverables", list);
  };
  const addDeliv = (k) =>
    setStage(k, "deliverables", [
      ...(draft.stages[k].deliverables || []),
      { id: "", name: "", status: "previsto", link: "" },
    ]);
  const removeDeliv = (k, idx) =>
    setStage(k, "deliverables", (draft.stages[k].deliverables || []).filter((_, i) => i !== idx));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300 * 1024) {
      toast.error("Imagem muito grande (máx. 300 KB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setField("logo", reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="mt-6 space-y-8 border-t border-white/10 pt-6" data-testid={`client-editor-${client.id}`}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <label className="block">
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Empresa</span>
          <input value={draft.company} onChange={(e) => setField("company", e.target.value)} data-testid={`client-company-${client.id}`} className={inputCls} />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Formato</span>
          <select value={draft.plan} onChange={(e) => setField("plan", e.target.value)} data-testid={`client-plan-${client.id}`} className={selectCls}>
            {PLANS.map((p) => (
              <option key={p} value={p} className="bg-ink">{p}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Investimento</span>
          <input value={draft.value} onChange={(e) => setField("value", e.target.value)} data-testid={`client-value-${client.id}`} className={inputCls} placeholder="Ex.: R$ 48.000 · 6x" />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Início</span>
          <input value={draft.start_date} onChange={(e) => setField("start_date", e.target.value)} data-testid={`client-start-${client.id}`} className={inputCls} placeholder="Ex.: mar/2026" />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Telefone</span>
          <input value={draft.phone} onChange={(e) => setField("phone", e.target.value)} data-testid={`client-phone-${client.id}`} className={inputCls} placeholder="(47) 99999-0000" />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">CNPJ</span>
          <input value={draft.cnpj} onChange={(e) => setField("cnpj", e.target.value)} data-testid={`client-cnpj-${client.id}`} className={inputCls} placeholder="00.000.000/0001-00" />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Contato financeiro</span>
          <input value={draft.financial_contact} onChange={(e) => setField("financial_contact", e.target.value)} data-testid={`client-financial-${client.id}`} className={inputCls} placeholder="Nome · e-mail · telefone" />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Status do projeto</span>
          <select value={draft.status} onChange={(e) => setField("status", e.target.value)} data-testid={`client-status-${client.id}`} className={selectCls}>
            <option value="ativo" className="bg-ink">Ativo</option>
            <option value="pausado" className="bg-ink">Pausado</option>
            <option value="encerrado" className="bg-ink">Encerrado</option>
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-5">
        <div>
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Logomarca do cliente (máx. 300 KB)
          </span>
          <input
            type="file"
            accept="image/*"
            onChange={onLogo}
            data-testid={`client-logo-${client.id}`}
            className="font-mono text-[11px] text-slate-400 file:mr-4 file:border file:border-white/20 file:bg-transparent file:px-4 file:py-2 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-white hover:file:border-brand-orange"
          />
        </div>
        {draft.logo && (
          <div className="flex items-center gap-3">
            <img src={draft.logo} alt="Logomarca" data-testid={`client-logo-preview-${client.id}`} className="h-12 w-auto max-w-[140px] border border-white/10 bg-white/5 object-contain p-1.5" />
            <button
              onClick={() => setField("logo", "")}
              title="Remover logomarca"
              className="flex h-8 w-8 items-center justify-center border border-white/15 text-slate-400 transition-colors hover:border-brand-red hover:text-brand-red"
            >
              <X size={13} />
            </button>
          </div>
        )}
      </div>

      {STAGE_KEYS.map((k) => {
        const st = draft.stages[k] || { status: "pendente", start: "", end: "", deliverables: [] };
        return (
          <div key={k} className="border border-white/10 p-5" data-testid={`client-stage-${k.toLowerCase()}-${client.id}`}>
            <div className="flex flex-wrap items-center gap-4">
              <span className="font-display text-xl font-extrabold text-outline-orange">{k}</span>
              <select
                value={st.status}
                onChange={(e) => setStage(k, "status", e.target.value)}
                data-testid={`stage-status-${k.toLowerCase()}-${client.id}`}
                className={selectCls + " w-auto"}
              >
                <option value="pendente" className="bg-ink">Pendente</option>
                <option value="ativa" className="bg-ink">Em andamento</option>
                <option value="concluida" className="bg-ink">Concluída</option>
              </select>
              <input
                value={st.start}
                onChange={(e) => setStage(k, "start", e.target.value)}
                placeholder="Início (ex.: 10/mar)"
                data-testid={`stage-start-${k.toLowerCase()}-${client.id}`}
                className={inputCls + " w-44"}
              />
              <input
                value={st.end}
                onChange={(e) => setStage(k, "end", e.target.value)}
                placeholder="Previsão (ex.: 24/mar)"
                data-testid={`stage-end-${k.toLowerCase()}-${client.id}`}
                className={inputCls + " w-44"}
              />
            </div>
            <textarea
              value={st.message || ""}
              onChange={(e) => setStage(k, "message", e.target.value)}
              placeholder="Mensagem para o cliente nesta etapa (opcional) — aparece no portal dele"
              data-testid={`stage-message-${k.toLowerCase()}-${client.id}`}
              rows={2}
              className={inputCls + " mt-3 resize-none"}
            />
            <div className="mt-4 space-y-2">
              {(st.deliverables || []).map((d, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-2">
                  <input
                    value={d.name}
                    onChange={(e) => setDeliv(k, idx, "name", e.target.value)}
                    placeholder="Nome do entregável"
                    data-testid={`deliv-name-${k.toLowerCase()}-${idx}-${client.id}`}
                    className={inputCls + " flex-1 min-w-[200px]"}
                  />
                  <select
                    value={d.status}
                    onChange={(e) => setDeliv(k, idx, "status", e.target.value)}
                    className={selectCls + " w-auto"}
                  >
                    <option value="previsto" className="bg-ink">Previsto</option>
                    <option value="producao" className="bg-ink">Em produção</option>
                    <option value="entregue" className="bg-ink">Entregue</option>
                  </select>
                  <input
                    value={d.link}
                    onChange={(e) => setDeliv(k, idx, "link", e.target.value)}
                    placeholder="Link (Drive, PDF...)"
                    className={inputCls + " flex-1 min-w-[180px]"}
                  />
                  <button
                    onClick={() => removeDeliv(k, idx)}
                    title="Remover entregável"
                    className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-400 transition-colors hover:border-brand-red hover:text-brand-red"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => addDeliv(k)}
                data-testid={`deliv-add-${k.toLowerCase()}-${client.id}`}
                className="flex items-center gap-2 border border-dashed border-white/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:border-brand-orange hover:text-brand-orange"
              >
                <Plus size={12} /> Adicionar entregável
              </button>
            </div>
          </div>
        );
      })}

      <button
        onClick={onSave}
        disabled={saving}
        data-testid={`client-save-${client.id}`}
        className="flex items-center gap-2 bg-brand-orange px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-brand-red disabled:opacity-50"
      >
        <Save size={14} /> {saving ? "Salvando..." : "Salvar projeto"}
      </button>
    </div>
  );
};

const ClientsPanel = ({ onUnauthorized }) => {
  const [clients, setClients] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const EMPTY_FORM = { name: "", email: "", password: "", company: "", plan: PLANS[0], value: "", start_date: "", phone: "", cnpj: "", financial_contact: "" };
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/clients`, { withCredentials: true });
      setClients(data.clients);
      const d = {};
      data.clients.forEach((c) => {
        d[c.id] = c.project || emptyProject(c.id);
      });
      setDrafts(d);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) onUnauthorized();
      else toast.error("Não foi possível carregar os clientes.");
    }
  }, [onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/clients`, form, { withCredentials: true });
      toast.success(
        data.email_sent
          ? `Cliente ${form.name} criado — e-mail de boas-vindas enviado para ${form.email}.`
          : `Cliente ${form.name} criado. O e-mail de boas-vindas não pôde ser enviado — compartilhe o acesso manualmente.`
      );
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) {
      const det = err.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Não foi possível criar o cliente.");
    } finally {
      setSaving(false);
    }
  };

  const save = async (clientId) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/projects/${clientId}`, drafts[clientId], { withCredentials: true });
      toast.success("Projeto atualizado.");
      load();
    } catch {
      toast.error("Não foi possível salvar o projeto.");
    } finally {
      setSaving(false);
    }
  };

  if (clients === null) {
    return (
      <div className="flex items-center justify-center gap-3 py-20 text-slate-500">
        <Loader2 size={18} className="animate-spin" /> Carregando clientes...
      </div>
    );
  }

  return (
    <div data-testid="clients-panel">
      <div className="flex justify-end">
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="client-new-toggle"
          className="flex items-center gap-2 border border-brand-orange/60 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-orange transition-colors hover:bg-brand-orange hover:text-white"
        >
          <UserPlus size={14} /> Novo cliente
        </button>
      </div>

      {showForm && (
        <motion.form
          onSubmit={create}
          data-testid="client-create-form"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 grid gap-4 border border-white/10 bg-navy/40 p-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Nome *</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-new-name" className={inputCls} />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">E-mail de acesso *</span>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-new-email" className={inputCls} />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Senha provisória *</span>
            <input required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="client-new-password" className={inputCls} />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Empresa</span>
            <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="client-new-company" className={inputCls} />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Telefone</span>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-new-phone" className={inputCls} placeholder="(47) 99999-0000" />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">CNPJ</span>
            <input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} data-testid="client-new-cnpj" className={inputCls} placeholder="00.000.000/0001-00" />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Contato financeiro</span>
            <input value={form.financial_contact} onChange={(e) => setForm({ ...form, financial_contact: e.target.value })} data-testid="client-new-financial" className={inputCls} placeholder="Nome · e-mail · telefone" />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Formato</span>
            <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className={selectCls}>
              {PLANS.map((p) => (
                <option key={p} value={p} className="bg-ink">{p}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Investimento</span>
            <input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputCls} placeholder="Ex.: R$ 48.000 · 6x" />
          </label>
          <div className="sm:col-span-2 lg:col-span-3">
            <button type="submit" disabled={saving} data-testid="client-create-submit" className="bg-brand-orange px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-brand-red disabled:opacity-50">
              {saving ? "Criando..." : "Criar acesso do cliente"}
            </button>
          </div>
        </motion.form>
      )}

      <div className="mt-6 space-y-4">
        {clients.length === 0 && (
          <div className="flex flex-col items-center gap-4 border border-dashed border-white/15 py-16 text-center">
            <UserPlus size={28} className="text-slate-600" />
            <p className="text-sm text-slate-500">Nenhum cliente cadastrado ainda.</p>
          </div>
        )}
        {clients.map((c) => (
          <div key={c.id} data-testid={`client-card-${c.id}`} className="border border-white/10 bg-navy/30 p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-display text-lg font-bold text-white">{c.name}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  {c.email} · {c.project?.company || "sem empresa"} · {c.project?.plan || "sem formato"}
                  {c.audit_count > 0 && ` · ${c.audit_count} auditoria${c.audit_count > 1 ? "s" : ""} (última: ${c.last_overall}/100)`}
                </p>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/agendar?ref=${c.id}`);
                  toast.success("Link de agendamento copiado. Envie ao cliente.");
                }}
                data-testid={`client-copy-link-${c.id}`}
                title="Copiar link de agendamento personalizado"
                className="flex items-center gap-2 border border-white/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
              >
                <Link2 size={14} /> Link
              </button>
              <button
                onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                data-testid={`client-expand-${c.id}`}
                className="flex items-center gap-2 border border-white/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
              >
                <ChevronDown size={14} className={`transition-transform ${expanded === c.id ? "rotate-180" : ""}`} />
                Gerenciar projeto
              </button>
            </div>
            {expanded === c.id && drafts[c.id] && (
              <ClientEditor
                client={c}
                draft={drafts[c.id]}
                onChange={(d) => setDrafts({ ...drafts, [c.id]: d })}
                onSave={() => save(c.id)}
                saving={saving}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Bookings tab ----------

const BOOKING_TYPE = {
  venda: ["Comercial · 30min", "border-brand-orange/50 text-brand-orange"],
  alinhamento: ["Alinhamento · 60min", "border-brand-magenta/50 text-brand-magenta"],
};
const WD_PT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

const fmtBookingDate = (d) => {
  const dt = new Date(d + "T12:00:00");
  return `${WD_PT[dt.getDay()]} ${dt.toLocaleDateString("pt-BR")}`;
};

const BookingsPanel = ({ onUnauthorized }) => {
  const [bookings, setBookings] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, { withCredentials: true });
      setBookings(data.bookings);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) onUnauthorized();
      else toast.error("Não foi possível carregar a agenda.");
    }
  }, [onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const cancel = async (b) => {
    try {
      await axios.delete(`${API}/admin/bookings/${b.id}`, { withCredentials: true });
      setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, status: "cancelado" } : x)));
      toast.success("Agendamento cancelado.");
    } catch {
      toast.error("Não foi possível cancelar.");
    }
  };

  const setStatus = async (b, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/bookings/${b.id}/status`, { status }, { withCredentials: true });
      setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, status } : x)));
      if (status === "confirmado") {
        toast.success(data.client_notified ? "Reunião confirmada — cliente avisado por e-mail." : "Reunião confirmada (e-mail não saiu).");
      } else {
        toast.success(
          data.client_notified
            ? "Pedido recusado — o lead recebeu um e-mail gentil com o diagnóstico gratuito."
            : "Pedido recusado. O horário foi liberado."
        );
      }
    } catch {
      toast.error("Não foi possível atualizar.");
    }
  };

  if (bookings === null) {
    return (
      <div className="flex items-center justify-center gap-3 py-20 text-slate-500">
        <Loader2 size={18} className="animate-spin" /> Carregando agenda...
      </div>
    );
  }

  const pending = bookings
    .filter((b) => b.status === "aguardando")
    .sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0));
  const upcoming = bookings.filter((b) => b.status === "confirmado");
  const cancelled = bookings.filter((b) => b.status === "cancelado" || b.status === "recusado");

  return (
    <div data-testid="bookings-panel" className="space-y-4">
      {pending.length > 0 && (
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-orange">
          {pending.length} pedido(s) aguardando confirmação
        </p>
      )}
      {upcoming.length === 0 && (
        <div className="flex flex-col items-center gap-4 border border-dashed border-white/15 py-16 text-center">
          <Calendar size={28} className="text-slate-600" />
          <p className="text-sm text-slate-500">Nenhum agendamento confirmado.</p>
        </div>
      )}
      {[...pending, ...upcoming].map((b) => {
        const [label, cls] = BOOKING_TYPE[b.type] || [b.type, "border-white/15 text-slate-400"];
        return (
          <div key={b.id} data-testid={`booking-card-${b.id}`} className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-navy/30 p-6">
            <div className="flex items-center gap-5">
              <span className="flex h-11 w-11 items-center justify-center border border-brand-orange/50 text-brand-orange">
                <Calendar size={17} />
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <p className="font-display text-lg font-bold text-white">{b.name}</p>
                  <span className={`border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] ${cls}`}>{label}</span>
                  {b.status === "aguardando" && (
                    <span className="border border-brand-orange/60 bg-brand-orange/10 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-brand-orange">
                      Aguardando confirmação
                    </span>
                  )}
                  {b.status === "aguardando" && b.lead_score != null && (
                    <span
                      data-testid={`booking-score-${b.id}`}
                      className={`border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] ${
                        b.lead_score >= 70
                          ? "border-emerald-400/50 text-emerald-400"
                          : b.lead_score >= 40
                            ? "border-brand-orange/50 text-brand-orange"
                            : "border-white/15 text-slate-500"
                      }`}
                      title="Score de aderência (0–100): faturamento, porte, urgência e orçamento"
                    >
                      Aderência {b.lead_score}
                    </span>
                  )}
                  {b.client_id && (
                    <span className="border border-emerald-400/40 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-emerald-400">
                      Cliente FUCHS
                    </span>
                  )}
                </div>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  {fmtBookingDate(b.date)} às {b.time} · {b.company || "sem empresa"}
                </p>
                <p className="mt-1 text-sm text-slate-400">
                  {b.email} · {b.whatsapp}
                </p>
                {b.type === "venda" && b.revenue && (
                  <p data-testid={`booking-qualif-${b.id}`} className="mt-2 border-l-2 border-brand-magenta pl-3 font-mono text-[10px] uppercase leading-relaxed tracking-[0.12em] text-slate-500">
                    {b.segment} · {b.revenue} · {b.employees} funcionários · {b.urgency} · {b.investment}
                  </p>
                )}
              </div>
            </div>
            {b.status === "aguardando" && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setStatus(b, "confirmado")}
                  data-testid={`booking-confirm-${b.id}`}
                  title="Confirmar reunião (cliente recebe e-mail)"
                  className="flex h-9 w-9 items-center justify-center border border-emerald-400/40 text-emerald-400 transition-colors hover:bg-emerald-400 hover:text-ink"
                >
                  <Check size={15} />
                </button>
                <button
                  onClick={() => setStatus(b, "recusado")}
                  data-testid={`booking-refuse-${b.id}`}
                  title="Recusar pedido (libera o horário)"
                  className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-400 transition-colors hover:border-brand-red hover:text-brand-red"
                >
                  <X size={15} />
                </button>
              </div>
            )}
            <button
              onClick={() => cancel(b)}
              data-testid={`booking-cancel-${b.id}`}
              title="Cancelar agendamento"
              className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-400 transition-colors hover:border-brand-red hover:text-brand-red"
            >
              <CalendarX size={15} />
            </button>
          </div>
        );
      })}
      {cancelled.length > 0 && (
        <p className="pt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
          {cancelled.length} cancelado(s)
        </p>
      )}
    </div>
  );
};

// ---------- Tracking links ----------

const TrackingLinks = ({ diagnostics, onUnauthorized }) => {
  const [links, setLinks] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/links`, { withCredentials: true });
      setLinks(data.links);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) onUnauthorized();
    }
  }, [onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/links`, { name }, { withCredentials: true });
      setName("");
      load();
      toast.success("Link de rastreamento gerado.");
    } catch {
      toast.error("Não foi possível gerar o link.");
    } finally {
      setSaving(false);
    }
  };

  const count = (slug) => diagnostics.filter((d) => d.source === slug).length;
  const urlFor = (slug) => `${window.location.origin}/diagnostico?fonte=${slug}`;

  return (
    <div data-testid="tracking-links" className="border border-white/10 bg-navy/30 p-6 lg:p-8">
      <h3 className="font-display text-lg font-bold text-white">Links de rastreamento para parceiros</h3>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">
        Gere uma URL exclusiva por parceiro. Quem responde o diagnóstico por ela fica marcado com a origem.
      </p>
      <form onSubmit={create} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          minLength={2}
          placeholder="Nome do parceiro (ex.: Contador Silva)"
          data-testid="tracking-link-name"
          className={inputCls}
        />
        <button
          type="submit"
          disabled={saving}
          data-testid="tracking-link-create"
          className="shrink-0 bg-brand-orange px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-red disabled:opacity-50"
        >
          Gerar link
        </button>
      </form>
      <div className="mt-5 space-y-3">
        {(links || []).map((l) => (
          <div key={l.id} data-testid={`tracking-link-${l.slug}`} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 p-4">
            <div>
              <p className="text-sm font-semibold text-white">{l.name}</p>
              <p className="mt-1 font-mono text-[11px] text-slate-500">/diagnostico?fonte={l.slug}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-brand-orange">
                {count(l.slug)} resposta(s)
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(urlFor(l.slug));
                  toast.success("Link copiado. Envie ao parceiro.");
                }}
                data-testid={`tracking-link-copy-${l.slug}`}
                className="flex items-center gap-2 border border-white/20 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
              >
                <Copy size={12} /> Copiar
              </button>
            </div>
          </div>
        ))}
        {links && links.length === 0 && (
          <p className="text-xs text-slate-600">Nenhum link gerado ainda.</p>
        )}
      </div>
    </div>
  );
};

// ---------- Partner report ----------

const leadWppLink = (lead) => {
  const digits = (lead.whatsapp || "").replace(/\D/g, "");
  if (!digits) return null;
  const full = digits.startsWith("55") ? digits : `55${digits}`;
  const first = lead.name ? lead.name.trim().split(" ")[0] : "";
  return `https://wa.me/${full}?text=${encodeURIComponent(
    `Olá ${first}, aqui é a Danielle, da FUCHS. Vi seu Diagnóstico 4D: índice geral ${lead.overall}/100. Vamos conversar sobre a leitura do seu radar?`
  )}`;
};

const RankIcon = ({ i }) =>
  i === 0 ? <Trophy size={14} className="text-brand-orange" /> : <Medal size={14} className="text-slate-500" />;

const PartnerReportPanel = ({ onUnauthorized }) => {
  const [groups, setGroups] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/partner-report`, { withCredentials: true });
      setGroups(data.groups);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) onUnauthorized();
      else toast.error("Não foi possível carregar o relatório.");
    }
  }, [onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  if (!groups) {
    return (
      <div className="flex items-center justify-center gap-3 py-20 text-slate-500">
        <Loader2 size={18} className="animate-spin" /> Carregando relatório...
      </div>
    );
  }

  return (
    <div data-testid="partner-report-panel" className="space-y-6">
      <div className="border border-white/10 bg-navy/30 p-6 lg:p-8">
        <h3 className="font-display text-lg font-bold text-white">Relatório por parceiro</h3>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">
          Maturidade média e melhores leads de cada link de rastreamento. O ranking segue o maior índice geral de maturidade.
        </p>
      </div>

      {groups.map((g) => (
        <div key={g.slug || "direto"} data-testid={`report-group-${g.slug || "direto"}`} className="border border-white/10 bg-navy/20 p-6 lg:p-8">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center border border-brand-orange/50 text-brand-orange">
                  <BarChart3 size={16} />
                </span>
                <div>
                  <h4 className="font-display text-xl font-bold text-white">{g.name}</h4>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    {g.slug && g.slug !== "direto" ? `/diagnostico?fonte=${g.slug}` : "respostas sem link de parceiro"}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-8">
              <div className="text-right">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Respostas</p>
                <p className="font-display text-2xl font-extrabold text-white" data-testid={`report-count-${g.slug}`}>{g.count}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Maturidade média</p>
                <p className={`font-display text-2xl font-extrabold ${levelColor(g.avg_overall)}`} data-testid={`report-avg-${g.slug}`}>
                  {g.count ? g.avg_overall : "—"}
                  {g.count > 0 && <span className="font-mono text-xs font-normal text-slate-500"> /100</span>}
                </p>
              </div>
              <a
                href={`${API}/admin/partner-report/${g.slug || "direto"}/pdf`}
                data-testid={`report-pdf-${g.slug || "direto"}`}
                title="Baixar relatório em PDF para enviar ao parceiro"
                className="flex items-center gap-2 self-center border border-white/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
              >
                <FileDown size={12} /> Baixar PDF
              </a>
            </div>
          </div>

          {g.count > 0 && (
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid={`report-dimensions-${g.slug}`}>
              {Object.entries(g.avg_dimensions).map(([dim, val]) => (
                <div key={dim}>
                  <div className="flex items-baseline justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{dim}</p>
                    <p className={`font-mono text-[11px] ${levelColor(val)}`}>{val}</p>
                  </div>
                  <div className="mt-1 h-1 w-full bg-white/10">
                    <div className="h-1 bg-brand-orange transition-all duration-500" style={{ width: `${val}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 border-t border-white/10 pt-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Melhores leads</p>
            {g.top_leads.length === 0 ? (
              <p className="mt-3 text-xs text-slate-600">Nenhuma resposta por este canal ainda.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {g.top_leads.map((lead, i) => {
                  const wpp = leadWppLink(lead);
                  return (
                    <div
                      key={lead.id}
                      data-testid={`report-lead-${lead.id}`}
                      className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-ink/40 px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex h-7 w-7 items-center justify-center border border-white/15">
                          <RankIcon i={i} />
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-white">{lead.name || "Sem identificação"}</p>
                          <p className="font-mono text-[10px] text-slate-500">
                            {lead.email || "—"}
                            {lead.whatsapp ? ` · ${lead.whatsapp}` : ""} · {fmtDate(lead.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`font-display text-lg font-extrabold ${levelColor(lead.overall)}`}>
                          {lead.overall}
                          <span className="font-mono text-[10px] font-normal text-slate-500"> /100</span>
                        </span>
                        {lead.email && (
                          <a
                            href={`mailto:${lead.email}`}
                            data-testid={`report-lead-email-${lead.id}`}
                            title="Responder por e-mail"
                            className="flex h-8 w-8 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-brand-orange hover:text-brand-orange"
                          >
                            <Mail size={13} />
                          </a>
                        )}
                        {wpp && (
                          <a
                            href={wpp}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`report-lead-whatsapp-${lead.id}`}
                            title="Responder pelo WhatsApp"
                            className="flex h-8 w-8 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-brand-orange hover:text-brand-orange"
                          >
                            <MessageCircle size={13} />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------- Articles (blog) ----------

const slugify = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const emptyArticle = {
  title: "", slug: "", headline: "", category: "perspectivas-fuchs",
  tagsText: "", pullQuote: "", pullQuoteAfter: 4,
  image: "", imageAlt: "", videoUrl: "", links: [], blocks: [{ t: "p", text: "" }],
  published: true,
};

const contentToBlocks = (content) =>
  (content || []).map((b) => {
    if (b.t === "list") return { t: "list", text: (b.items || []).join("\n") };
    if (b.t === "numlist") return { t: "numlist", text: (b.items || []).map((it) => `${it.lead} | ${it.text}`).join("\n") };
    return { t: b.t, text: b.text || "" };
  });

const blocksToContent = (blocks) =>
  blocks
    .map((b) => {
      if (b.t === "list") {
        return { t: "list", items: b.text.split("\n").map((s) => s.trim()).filter(Boolean) };
      }
      if (b.t === "numlist") {
        return {
          t: "numlist",
          items: b.text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
            const i = l.indexOf("|");
            return i >= 0 ? { lead: l.slice(0, i).trim(), text: l.slice(i + 1).trim() } : { lead: "", text: l };
          }),
        };
      }
      return { t: b.t, text: b.text.trim() };
    })
    .filter((b) => (b.items ? b.items.length > 0 : b.text));

const artInput = "w-full border border-white/10 bg-ink px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-brand-orange focus:outline-none";
const artLabel = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500";

const BLOCK_TYPES = [
  ["p", "Parágrafo"],
  ["h2", "Subtítulo"],
  ["list", "Lista (1 item por linha)"],
  ["numlist", "Lista numerada (Título | descrição por linha)"],
];

const ArticleEditor = ({ initial, onCancel, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [slugTouched, setSlugTouched] = useState(!!initial.slug);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setBlock = (i, patch) =>
    set("blocks", form.blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.blocks.length) return;
    const blocks = [...form.blocks];
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    set("blocks", blocks);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("O texto precisa de um título.");
      return;
    }
    if (blocksToContent(form.blocks).length === 0) {
      toast.error("O texto precisa de ao menos um bloco de conteúdo.");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim() || slugify(form.title),
      headline: form.headline.trim(),
      category: form.category,
      tags: form.tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      pullQuote: form.pullQuote.trim(),
      pullQuoteAfter: Number(form.pullQuoteAfter) || 4,
      image: form.image.trim(),
      imageAlt: form.imageAlt.trim(),
      videoUrl: form.videoUrl.trim(),
      links: form.links.filter((l) => l.label.trim() && l.url.trim()),
      content: blocksToContent(form.blocks),
      published: form.published,
    };
    try {
      if (form.id) {
        await axios.put(`${API}/admin/articles/${form.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/articles`, payload, { withCredentials: true });
      }
      toast.success(form.id ? "Texto atualizado." : "Texto publicado. Leitores inscritos foram avisados por e-mail.");
      onSaved();
    } catch (err) {
      if (err.response?.status === 409) toast.error(err.response.data.detail);
      else toast.error("Não foi possível salvar o texto.");
      setSaving(false);
    }
  };

  return (
    <div data-testid="article-editor" className="space-y-8 border border-white/10 bg-navy/20 p-6 lg:p-8">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-white">{form.id ? "Editar texto" : "Novo texto"}</h3>
        <button onClick={onCancel} data-testid="article-cancel" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-white">
          Cancelar
        </button>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="lg:col-span-2">
          <label className={artLabel}>Título *</label>
          <input
            className={artInput}
            value={form.title}
            data-testid="article-field-title"
            onChange={(e) => {
              set("title", e.target.value);
              if (!slugTouched) set("slug", slugify(e.target.value));
            }}
            placeholder="Título do texto"
          />
        </div>
        <div>
          <label className={artLabel}>Endereço (slug)</label>
          <input
            className={artInput}
            value={form.slug}
            data-testid="article-field-slug"
            onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }}
            placeholder="gerado-automaticamente"
          />
          <p className="mt-1 font-mono text-[10px] text-slate-600">/conteudo/{form.slug || "…"}</p>
        </div>
        <div>
          <label className={artLabel}>Categoria</label>
          <select
            className={artInput}
            value={form.category}
            data-testid="article-field-category"
            onChange={(e) => set("category", e.target.value)}
          >
            {BLOG_CATEGORIES.map((c) => (
              <option key={c.slug} value={c.slug}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="lg:col-span-2">
          <label className={artLabel}>Headline de apoio</label>
          <textarea className={`${artInput} min-h-20`} value={form.headline} data-testid="article-field-headline" onChange={(e) => set("headline", e.target.value)} placeholder="Frase em itálico logo abaixo do título" />
        </div>
        <div>
          <label className={artLabel}>Frase de destaque</label>
          <textarea className={`${artInput} min-h-20`} value={form.pullQuote} data-testid="article-field-pullquote" onChange={(e) => set("pullQuote", e.target.value)} placeholder="Citação que aparece em destaque no meio do texto" />
        </div>
        <div>
          <label className={artLabel}>Tags (separadas por vírgula)</label>
          <input className={artInput} value={form.tagsText} data-testid="article-field-tags" onChange={(e) => set("tagsText", e.target.value)} placeholder="posicionamento, marca" />
          <label className={`${artLabel} mt-4`}>Frase de destaque após o bloco nº</label>
          <input type="number" min="0" className={artInput} value={form.pullQuoteAfter} data-testid="article-field-pullquote-after" onChange={(e) => set("pullQuoteAfter", e.target.value)} />
        </div>
        <div>
          <label className={artLabel}>Foto (URL)</label>
          <input className={artInput} value={form.image} data-testid="article-field-image" onChange={(e) => set("image", e.target.value)} placeholder="https://…" />
          {form.image && <img src={form.image} alt="" className="mt-3 aspect-[16/9] w-full border border-white/10 object-cover" />}
        </div>
        <div>
          <label className={artLabel}>Descrição da foto (alt)</label>
          <input className={artInput} value={form.imageAlt} onChange={(e) => set("imageAlt", e.target.value)} placeholder="O que a imagem representa" />
          <label className={`${artLabel} mt-4`}>Ou link de vídeo (YouTube embed — substitui a foto)</label>
          <input className={artInput} value={form.videoUrl} data-testid="article-field-video" onChange={(e) => set("videoUrl", e.target.value)} placeholder="https://www.youtube.com/embed/…" />
        </div>
      </div>

      {/* Links de referência */}
      <div>
        <div className="flex items-center justify-between">
          <label className={artLabel}>Links de referência</label>
          <button
            onClick={() => set("links", [...form.links, { label: "", url: "" }])}
            data-testid="article-add-link"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-orange hover:text-white"
          >
            <Plus size={12} /> Adicionar link
          </button>
        </div>
        <div className="mt-2 space-y-2">
          {form.links.map((l, i) => (
            <div key={i} className="flex flex-col gap-2 sm:flex-row">
              <input className={artInput} value={l.label} placeholder="Rótulo (ex.: Também publicado no Portal X)" onChange={(e) => set("links", form.links.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <input className={artInput} value={l.url} placeholder="https://…" onChange={(e) => set("links", form.links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))} />
              <button onClick={() => set("links", form.links.filter((_, j) => j !== i))} className="shrink-0 border border-white/15 px-3 text-slate-500 transition-colors hover:border-brand-red hover:text-brand-red" title="Remover link">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Blocos de conteúdo */}
      <div>
        <label className={artLabel}>Conteúdo (em blocos, na ordem de leitura)</label>
        <div className="mt-2 space-y-3">
          {form.blocks.map((b, i) => (
            <div key={i} data-testid={`article-block-${i}`} className="border border-white/10 bg-ink/50 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Bloco {i + 1}</span>
                <div className="flex items-center gap-1">
                  <select className="border border-white/10 bg-ink px-2 py-1.5 text-[11px] text-slate-300" value={b.t} data-testid={`article-block-type-${i}`} onChange={(e) => setBlock(i, { t: e.target.value })}>
                    {BLOCK_TYPES.map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                  <button onClick={() => moveBlock(i, -1)} className="border border-white/15 p-1.5 text-slate-400 hover:border-brand-orange hover:text-brand-orange" title="Subir"><ArrowUp size={12} /></button>
                  <button onClick={() => moveBlock(i, 1)} className="border border-white/15 p-1.5 text-slate-400 hover:border-brand-orange hover:text-brand-orange" title="Descer"><ArrowDown size={12} /></button>
                  <button onClick={() => set("blocks", form.blocks.filter((_, j) => j !== i))} className="border border-white/15 p-1.5 text-slate-400 hover:border-brand-red hover:text-brand-red" title="Remover bloco"><X size={12} /></button>
                </div>
              </div>
              <textarea
                className={`${artInput} ${b.t === "h2" ? "min-h-14" : "min-h-24"}`}
                value={b.text}
                data-testid={`article-block-text-${i}`}
                onChange={(e) => setBlock(i, { text: e.target.value })}
                placeholder={b.t === "list" ? "Um item por linha" : b.t === "numlist" ? "Título do sinal | texto de apoio (um por linha)" : "Escreva aqui…"}
              />
            </div>
          ))}
        </div>
        <button
          onClick={() => set("blocks", [...form.blocks, { t: "p", text: "" }])}
          data-testid="article-add-block"
          className="mt-3 inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
        >
          <Plus size={12} /> Adicionar bloco
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6">
        <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-300">
          <input type="checkbox" checked={form.published} data-testid="article-field-published" onChange={(e) => set("published", e.target.checked)} className="h-4 w-4 accent-brand-orange" />
          Publicado (visível no site)
        </label>
        <button
          onClick={save}
          disabled={saving}
          data-testid="article-save-button"
          className="inline-flex items-center gap-2 bg-brand-orange px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-red disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {form.id ? "Salvar alterações" : "Publicar texto"}
        </button>
      </div>
    </div>
  );
};

const ArticlesPanel = ({ onUnauthorized }) => {
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState(null);
  const [editing, setEditing] = useState(null); // null = lista, objeto = editor

  const load = useCallback(async () => {
    try {
      const [a, n] = await Promise.all([
        axios.get(`${API}/admin/articles`, { withCredentials: true }),
        axios.get(`${API}/admin/newsletter`, { withCredentials: true }),
      ]);
      setData(a.data.articles);
      setSubs(n.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) onUnauthorized();
      else toast.error("Não foi possível carregar os textos.");
    }
  }, [onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Excluir "${a.title}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/admin/articles/${a.id}`, { withCredentials: true });
      toast.success("Texto excluído.");
      load();
    } catch {
      toast.error("Não foi possível excluir.");
    }
  };

  if (editing) {
    return <ArticleEditor initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  }

  if (!data || !subs) {
    return (
      <div className="flex items-center justify-center gap-3 py-20 text-slate-500">
        <Loader2 size={18} className="animate-spin" /> Carregando conteúdo...
      </div>
    );
  }

  return (
    <div data-testid="articles-panel" className="space-y-10">
      <div className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-navy/30 p-6 lg:p-8">
        <div>
          <h3 className="font-display text-lg font-bold text-white">Textos do blog</h3>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            Publique e edite os textos de /conteudo. Ao publicar um texto novo, os leitores inscritos recebem o aviso por e-mail.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...emptyArticle })}
          data-testid="article-new-button"
          className="inline-flex items-center gap-2 bg-brand-orange px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-red"
        >
          <Plus size={14} /> Novo texto
        </button>
      </div>

      <div className="space-y-3">
        {data.map((a) => {
          const cat = BLOG_CATEGORIES.find((c) => c.slug === a.category);
          return (
            <div key={a.id} data-testid={`article-row-${a.slug}`} className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-navy/20 px-6 py-5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="truncate font-display text-base font-bold text-white">{a.title}</p>
                  {!a.published && (
                    <span className="border border-brand-orange/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-brand-orange">Rascunho</span>
                  )}
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  {cat?.name || a.category} · /conteudo/{a.slug}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a href={`/conteudo/${a.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`article-view-${a.slug}`} title="Ver no site" className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-brand-orange hover:text-brand-orange">
                  <Eye size={14} />
                </a>
                <button onClick={() => setEditing({ ...a, tagsText: (a.tags || []).join(", "), blocks: contentToBlocks(a.content) })} data-testid={`article-edit-${a.slug}`} title="Editar" className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-brand-orange hover:text-brand-orange">
                  <Pencil size={14} />
                </button>
                <button onClick={() => remove(a)} data-testid={`article-delete-${a.slug}`} title="Excluir" className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-brand-red hover:text-brand-red">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
        })}
        {data.length === 0 && (
          <p className="border border-white/10 bg-navy/20 px-6 py-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
            Nenhum texto publicado ainda
          </p>
        )}
      </div>

      {/* Leitores inscritos */}
      <div className="border border-white/10 bg-navy/30 p-6 lg:p-8">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center border border-brand-orange/50 text-brand-orange">
            <Users size={16} />
          </span>
          <div>
            <h3 className="font-display text-lg font-bold text-white">Leitores inscritos</h3>
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
              <span data-testid="subscribers-active-count">{subs.active}</span> ativos · {subs.total} no total
            </p>
          </div>
        </div>
        <div className="mt-5 space-y-2" data-testid="subscribers-list">
          {subs.subscribers.length === 0 && (
            <p className="text-xs text-slate-600">Nenhum leitor inscrito ainda. A caixa de inscrição fica ao final de cada texto.</p>
          )}
          {subs.subscribers.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 border border-white/10 bg-ink/40 px-4 py-3">
              <p className="text-sm text-white">{s.email}</p>
              <p className="font-mono text-[10px] text-slate-500">
                {s.article ? `via /${s.article} · ` : ""}{fmtDate(s.created_at)} · {s.active === false ? "saiu da lista" : "ativo"}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------- Panel ----------

const Panel = ({ user, onLogout }) => {
  const [tab, setTab] = useState("mensagens");
  const [messages, setMessages] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/messages`, { withCredentials: true });
      setMessages(data.messages);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) onLogout();
      else toast.error("Não foi possível carregar as mensagens.");
    }
    try {
      const { data } = await axios.get(`${API}/admin/diagnostics`, { withCredentials: true });
      setDiagnostics(data.diagnostics);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRead = async (m) => {
    try {
      await axios.patch(`${API}/admin/messages/${m.id}/read`, { read: !m.read }, { withCredentials: true });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: !m.read } : x)));
    } catch {
      toast.error("Não foi possível atualizar a mensagem.");
    }
  };

  const remove = async (m) => {
    try {
      await axios.delete(`${API}/admin/messages/${m.id}`, { withCredentials: true });
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Mensagem excluída.");
    } catch {
      toast.error("Não foi possível excluir.");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      onLogout();
    }
  };

  const unread = messages.filter((m) => !m.read).length;

  return (
    <div data-testid="admin-panel" className="mx-auto w-full max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <SectionLabel>Painel</SectionLabel>
          <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Olá, Danielle.
          </h1>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
            {messages.length} mensagens · {unread} não lidas · {diagnostics.length} diagnósticos
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={load}
            data-testid="admin-refresh-button"
            className="flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
          >
            <RefreshCw size={14} /> Atualizar
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:border-brand-red hover:text-brand-red"
          >
            <LogOut size={14} /> Sair
          </button>
        </div>
      </div>

      <div className="mt-10 flex gap-2 border-b border-white/10">
        {[
          ["mensagens", "Mensagens", messages.length],
          ["diagnosticos", "Diagnósticos 4D", diagnostics.length],
          ["clientes", "Clientes", null],
          ["agenda", "Agenda", null],
          ["relatorios", "Relatórios", null],
          ["conteudo", "Conteúdo", null],
        ].map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`admin-tab-${key}`}
            className={`relative px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
              tab === key ? "text-white" : "text-slate-500 hover:text-white"
            }`}
          >
            {label} {count !== null && <span className="text-brand-orange">{count}</span>}
            {tab === key && (
              <motion.span layoutId="admin-tab-line" className="absolute inset-x-0 -bottom-px h-0.5 bg-brand-orange" />
            )}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-5">
        {tab === "agenda" ? (
          <BookingsPanel onUnauthorized={onLogout} />
        ) : tab === "clientes" ? (
          <ClientsPanel onUnauthorized={onLogout} />
        ) : tab === "relatorios" ? (
          <PartnerReportPanel onUnauthorized={onLogout} />
        ) : tab === "conteudo" ? (
          <ArticlesPanel onUnauthorized={onLogout} />
        ) : loading ? (
          <div className="flex items-center justify-center gap-3 py-20 text-slate-500">
            <Loader2 size={18} className="animate-spin" /> Carregando...
          </div>
        ) : tab === "mensagens" ? (
          messages.length === 0 ? (
            <div data-testid="admin-empty-state" className="flex flex-col items-center gap-4 border border-dashed border-white/15 py-20 text-center">
              <Inbox size={32} className="text-slate-600" />
              <p className="text-sm text-slate-500">Nenhuma mensagem recebida ainda.</p>
            </div>
          ) : (
            messages.map((m) => (
              <MessageCard key={m.id} m={m} onToggleRead={toggleRead} onDelete={remove} />
            ))
          )
        ) : (
          <div className="space-y-10">
            <TrackingLinks diagnostics={diagnostics} onUnauthorized={onLogout} />
            {diagnostics.length === 0 ? (
              <div data-testid="admin-diagnostics-empty" className="flex flex-col items-center gap-4 border border-dashed border-white/15 py-20 text-center">
                <Radar size={32} className="text-slate-600" />
                <p className="text-sm text-slate-500">Nenhum diagnóstico 4D respondido ainda.</p>
              </div>
            ) : (
              diagnostics.map((d) => <DiagnosticCard key={d.id} d={d} />)
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const [auth, setAuth] = useState(undefined);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setAuth(data))
      .catch(() => setAuth(null));
  }, []);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen bg-ink px-6 pb-24 pt-36 lg:px-10 lg:pt-44"
    >
      {auth === undefined ? (
        <div className="flex items-center justify-center gap-3 py-24 text-slate-500">
          <Loader2 size={18} className="animate-spin" /> Verificando acesso...
        </div>
      ) : auth === null ? (
        <LoginForm title="Área de Gerenciamento" prefix="admin" onSuccess={setAuth} />
      ) : (
        <Panel user={auth} onLogout={() => setAuth(null)} />
      )}
    </motion.main>
  );
}
